// ============================================================================
// HiringActionCard — a step on one hiring request from Ask AI (item #944, the
// hiring part), built like the other part cards to Ben's approved card demo
// (docs/build-room/demos/item-944.html). What each action shows is
// hiringCard() in ../hiring-actions.ts; the save is commitHiringAction().
// ============================================================================

import { Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CopilotProposalBase } from '../../../shared/types';
import { hiringCard, hiringOf, type HiringDraft } from '../hiring-actions';
import { CardShell } from './card-shell';

interface Props {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function HiringActionCard({ proposal, isCommitting, onDoIt, onDiscard }: Props) {
  const draft = proposal.input as unknown as HiringDraft;
  const fields = draft.fields ?? {};
  const card = hiringCard(draft, fields);

  return (
    <CardShell
      title={`${card.title} · ${hiringOf(draft)}`}
      icon={<Briefcase className="h-4 w-4" />}
      accentClass={card.readBack ? 'border-st-warn-border-strong' : 'border-tcs-border'}
      status={proposal.status}
      committedLabel="Saved"
      isCommitting={isCommitting}
      doItDisabled={!!card.missing}
      doItDisabledReason={card.missing ?? undefined}
      readBack={card.readBack ?? undefined}
      doItLabel={card.doItLabel ?? undefined}
      onDoIt={() => onDoIt({ ...draft, fields }, false)}
      onDiscard={onDiscard}
      after={
        <Link to={`/people/hiring?open=${encodeURIComponent(draft.request_id)}`} className="text-sm font-medium text-tcs-text underline">
          Open the request
        </Link>
      }
    >
      {card.info.map((i) => (
        <div key={i.label} className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-tcs-border px-2 py-1.5 text-sm">
          <span className="text-xs text-tcs-text-muted">{i.label}</span>
          <span className="text-right text-tcs-text">{i.value}</span>
        </div>
      ))}
    </CardShell>
  );
}
